/**
 * The opt-in copy of the admin's LangSmith key on this device (GEN5-5344, K2).
 *
 * The key is encrypted with AES-GCM before it is stored. Two protections:
 * - "device": a random AES key the browser creates as NON-EXTRACTABLE and keeps
 *   in IndexedDB beside the ciphertext. Script on this page can use it but
 *   never read its bytes, so a copy of the storage files or a dump of
 *   localStorage does not reveal the LangSmith key. It does not protect against
 *   script running in this page (XSS) or someone using this profile unlocked.
 * - "passphrase": the AES key is derived from a passphrase (PBKDF2-SHA256,
 *   600,000 iterations, a random 16-byte salt) each time it is unlocked. The
 *   passphrase and the derived key are never stored.
 *
 * Nothing here throws to its caller: each operation returns an outcome, and a
 * failure carries a `VaultFailure` the screen turns into a sentence. No
 * plaintext key ever goes into a failure, a log or an error message.
 */

export type RememberedKey = {
  apiKey: string;
  apiUrl: string | null;
  workspaceId: string | null;
};

export type Protection =
  | { kind: "device" }
  | { kind: "passphrase"; passphrase: string };

export type ProtectionKind = Protection["kind"];

/** Why the vault could not do what was asked; `detail` is the browser's own reason, never the key. */
export type VaultFailure =
  | { kind: "noIndexedDb" }
  | { kind: "noWebCrypto" }
  | { kind: "storageRefused"; detail: string }
  | { kind: "storageFull" }
  | { kind: "writeFailed"; detail: string }
  | { kind: "verifyFailed" }
  | { kind: "notDecryptable" }
  | { kind: "notReadable" }
  | { kind: "wipeFailed"; detail: string };

/** The stored passphrase record, opaque to callers: only `unlockRememberedKey` reads it. */
export type LockedRecord = {
  readonly kind: "passphrase";
  readonly salt: Uint8Array<ArrayBuffer>;
  readonly iterations: number;
  readonly iv: Uint8Array<ArrayBuffer>;
  readonly ciphertext: ArrayBuffer;
};

export type ReadOutcome =
  | { status: "empty" }
  | { status: "remembered"; key: RememberedKey }
  | { status: "locked"; record: LockedRecord }
  | { status: "failed"; failure: VaultFailure };

export type SaveOutcome =
  | { status: "saved" }
  | { status: "failed"; failure: VaultFailure };

export type UnlockOutcome =
  | { status: "unlocked"; key: RememberedKey }
  | { status: "wrongPassphrase" }
  | { status: "failed"; failure: VaultFailure };

export type WipeOutcome =
  | { status: "wiped" }
  | { status: "failed"; failure: VaultFailure };

/** OWASP's 2023 floor for PBKDF2-HMAC-SHA256; a stored record with fewer is refused (a downgrade). */
export const PBKDF2_ITERATIONS = 600_000;
const SALT_BYTES = 16;
const IV_BYTES = 12;
/** Binds the ciphertext to its purpose, so a blob from elsewhere does not decrypt as a key. */
const ADDITIONAL_DATA = new TextEncoder().encode("genfive-langsmith-key/1");

export const VAULT_DATABASE = "genfive-admin-langsmith-key";
const DATABASE_VERSION = 1;
const STORE = "vault";
const RECORD_ID = "langsmith";
const RECORD_VERSION = 1;

/** Raised inside this module only; the boundary turns it into a `VaultFailure`. */
export class LangSmithKeyVaultError extends Error {
  override readonly name = "LangSmithKeyVaultError";

  constructor(
    public readonly failure: VaultFailure,
    public readonly cause?: unknown
  ) {
    super(`LangSmith key vault: ${failure.kind}`);
    if (cause instanceof Error && cause.stack) {
      this.stack = `${this.stack}\nCaused by: ${cause.stack}`;
    }
    Object.setPrototypeOf(this, LangSmithKeyVaultError.prototype);
  }
}

/** The browser's reason in a few words (its name and message), never a value we gave it. */
const browserReason = (error: unknown): string => {
  if (error instanceof DOMException || error instanceof Error) {
    const message = error.message.trim();
    return message === "" ? error.name : `${error.name}: ${message}`;
  }
  return "no reason given";
};

const isQuotaError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === "QuotaExceededError";

const toFailure = (
  error: unknown,
  fallback: "storageRefused" | "writeFailed"
): VaultFailure => {
  if (error instanceof LangSmithKeyVaultError) return error.failure;
  if (isQuotaError(error)) return { kind: "storageFull" };
  return { kind: fallback, detail: browserReason(error) };
};

/** What this browser lacks for the vault, or null when it has both IndexedDB and WebCrypto. */
export const vaultUnavailableReason = (): VaultFailure | null => {
  if (typeof indexedDB === "undefined" || indexedDB === null) {
    return { kind: "noIndexedDb" };
  }
  if (
    typeof crypto === "undefined" ||
    typeof crypto.subtle === "undefined" ||
    typeof crypto.getRandomValues !== "function"
  ) {
    return { kind: "noWebCrypto" };
  }
  return null;
};

// ---------------------------------------------------------------- IndexedDB

const requestResult = <Result>(request: IDBRequest<Result>): Promise<Result> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("IndexedDB request failed"));
  });

const openDatabase = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(VAULT_DATABASE, DATABASE_VERSION);
    } catch (error) {
      // Some browsers throw here (a sandboxed frame, blocked site data) rather than fail the request.
      reject(
        new LangSmithKeyVaultError(
          { kind: "storageRefused", detail: browserReason(error) },
          error
        )
      );
      return;
    }
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(
        new LangSmithKeyVaultError(
          { kind: "storageRefused", detail: browserReason(request.error) },
          request.error
        )
      );
    request.onblocked = () =>
      reject(
        new LangSmithKeyVaultError({
          kind: "storageRefused",
          detail: "another tab holds an older copy of this storage open",
        })
      );
  });

/** One transaction, closed afterwards; resolves only once a write is durable (the transaction completed). */
const inStore = async <Result>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => Promise<Result>
): Promise<Result> => {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE, mode);
    const completed = new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () =>
        reject(transaction.error ?? new Error("IndexedDB transaction failed"));
      transaction.onabort = () =>
        reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
    });
    const [result] = await Promise.all([
      run(transaction.objectStore(STORE)),
      completed,
    ]);
    return result;
  } finally {
    database.close();
  }
};

// ------------------------------------------------------- stored record shape

type DeviceRecord = {
  kind: "device";
  wrappingKey: CryptoKey;
  iv: Uint8Array<ArrayBuffer>;
  ciphertext: ArrayBuffer;
};

type StoredRecord = DeviceRecord | LockedRecord;

const encodeRecord = (record: StoredRecord): Record<string, unknown> => ({
  version: RECORD_VERSION,
  ...record,
  savedAt: Date.now(),
});

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Realm-independent: a value cloned by IndexedDB may come from another realm than this page's constructors. */
const isArrayBuffer = (value: unknown): value is ArrayBuffer =>
  Object.prototype.toString.call(value) === "[object ArrayBuffer]";

const bytesOf = (
  value: unknown,
  length?: number
): Uint8Array<ArrayBuffer> | null => {
  if (
    !ArrayBuffer.isView(value) ||
    Object.prototype.toString.call(value) !== "[object Uint8Array]"
  ) {
    return null;
  }
  // A fresh copy, so the bytes are ours and backed by a plain ArrayBuffer of this realm.
  const copy = new Uint8Array(value.byteLength);
  copy.set(new Uint8Array(value.buffer, value.byteOffset, value.byteLength));
  return length === undefined || copy.byteLength === length ? copy : null;
};

const bufferOf = (value: unknown): ArrayBuffer | null => {
  if (isArrayBuffer(value)) {
    const copy = new Uint8Array(value.byteLength);
    copy.set(new Uint8Array(value));
    return copy.buffer;
  }
  const bytes = bytesOf(value);
  return bytes === null ? null : bytes.buffer;
};

const isUsableWrappingKey = (value: unknown): value is CryptoKey =>
  typeof CryptoKey !== "undefined" &&
  value instanceof CryptoKey &&
  value.algorithm.name === "AES-GCM" &&
  value.usages.includes("decrypt");

/** Read back as unknown: the browser holds whatever was written, perhaps by another build or by hand. */
const decodeRecord = (value: unknown): StoredRecord | null => {
  if (!isObject(value) || value.version !== RECORD_VERSION) return null;
  const iv = bytesOf(value.iv, IV_BYTES);
  const ciphertext = bufferOf(value.ciphertext);
  if (iv === null || ciphertext === null) return null;
  if (value.kind === "device") {
    return isUsableWrappingKey(value.wrappingKey)
      ? { kind: "device", wrappingKey: value.wrappingKey, iv, ciphertext }
      : null;
  }
  if (value.kind === "passphrase") {
    const salt = bytesOf(value.salt, SALT_BYTES);
    const iterations = value.iterations;
    if (
      salt === null ||
      typeof iterations !== "number" ||
      !Number.isInteger(iterations) ||
      iterations < PBKDF2_ITERATIONS
    ) {
      return null;
    }
    return { kind: "passphrase", salt, iterations, iv, ciphertext };
  }
  return null;
};

/** The decrypted plaintext is ours, but is still read field by field. */
const decodeKey = (plaintext: ArrayBuffer): RememberedKey | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(plaintext));
  } catch {
    // Not JSON: treated exactly like any other unreadable record (the caller says so).
    return null;
  }
  if (
    !isObject(parsed) ||
    typeof parsed.apiKey !== "string" ||
    parsed.apiKey === ""
  )
    return null;
  const apiUrl = parsed.apiUrl;
  const workspaceId = parsed.workspaceId;
  if (
    (apiUrl !== null && typeof apiUrl !== "string") ||
    (workspaceId !== null && typeof workspaceId !== "string")
  ) {
    return null;
  }
  return { apiKey: parsed.apiKey, apiUrl, workspaceId };
};

const encodeKey = (key: RememberedKey): Uint8Array<ArrayBuffer> =>
  new TextEncoder().encode(
    JSON.stringify({
      apiKey: key.apiKey,
      apiUrl: key.apiUrl,
      workspaceId: key.workspaceId,
    })
  );

const sameKey = (left: RememberedKey, right: RememberedKey): boolean =>
  left.apiKey === right.apiKey &&
  left.apiUrl === right.apiUrl &&
  left.workspaceId === right.workspaceId;

// ----------------------------------------------------------------- WebCrypto

const randomBytes = (length: number): Uint8Array<ArrayBuffer> =>
  crypto.getRandomValues(new Uint8Array(length));

const derivePassphraseKey = async (
  passphrase: string,
  salt: Uint8Array<ArrayBuffer>,
  iterations: number
): Promise<CryptoKey> => {
  const material = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(passphrase),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", hash: "SHA-256", salt, iterations },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
};

const encrypt = (
  aesKey: CryptoKey,
  iv: Uint8Array<ArrayBuffer>,
  plaintext: Uint8Array<ArrayBuffer>
): Promise<ArrayBuffer> =>
  crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: ADDITIONAL_DATA },
    aesKey,
    plaintext
  );

/** Null when AES-GCM refuses: a wrong key, or a ciphertext or iv that was changed. */
const decrypt = async (
  aesKey: CryptoKey,
  iv: Uint8Array<ArrayBuffer>,
  ciphertext: ArrayBuffer
): Promise<ArrayBuffer | null> => {
  try {
    return await crypto.subtle.decrypt(
      { name: "AES-GCM", iv, additionalData: ADDITIONAL_DATA },
      aesKey,
      ciphertext
    );
  } catch {
    // AES-GCM gives one answer for every mismatch (OperationError); the caller names it.
    return null;
  }
};

const decryptKey = async (
  aesKey: CryptoKey,
  record: StoredRecord
): Promise<RememberedKey | null> => {
  const plaintext = await decrypt(aesKey, record.iv, record.ciphertext);
  return plaintext === null ? null : decodeKey(plaintext);
};

const sealKey = async (
  key: RememberedKey,
  protection: Protection
): Promise<{ record: StoredRecord; aesKey: CryptoKey }> => {
  const iv = randomBytes(IV_BYTES);
  const plaintext = encodeKey(key);
  if (protection.kind === "device") {
    const wrappingKey = await crypto.subtle.generateKey(
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"]
    );
    const ciphertext = await encrypt(wrappingKey, iv, plaintext);
    plaintext.fill(0);
    return {
      record: { kind: "device", wrappingKey, iv, ciphertext },
      aesKey: wrappingKey,
    };
  }
  const salt = randomBytes(SALT_BYTES);
  const aesKey = await derivePassphraseKey(
    protection.passphrase,
    salt,
    PBKDF2_ITERATIONS
  );
  const ciphertext = await encrypt(aesKey, iv, plaintext);
  plaintext.fill(0);
  return {
    record: {
      kind: "passphrase",
      salt,
      iterations: PBKDF2_ITERATIONS,
      iv,
      ciphertext,
    },
    aesKey,
  };
};

// ------------------------------------------------------------ the operations

const readRecord = async (): Promise<
  { found: false } | { found: true; record: StoredRecord | null }
> => {
  const stored = await inStore("readonly", (store) =>
    requestResult(store.get(RECORD_ID))
  );
  return stored === undefined
    ? { found: false }
    : { found: true, record: decodeRecord(stored) };
};

/** A copy that does not read back as the key is removed, so the next visit does not offer it. */
const notVerified = async (): Promise<SaveOutcome> => {
  await forgetRememberedKey();
  return { status: "failed", failure: { kind: "verifyFailed" } };
};

/**
 * Encrypts and stores the key, replacing any earlier copy, then reads it back
 * and decrypts it: "saved" only when what comes back is the key we were given.
 */
export const rememberKey = async (
  key: RememberedKey,
  protection: Protection
): Promise<SaveOutcome> => {
  const unavailable = vaultUnavailableReason();
  if (unavailable !== null) return { status: "failed", failure: unavailable };
  try {
    const { record, aesKey } = await sealKey(key, protection);
    await inStore("readwrite", (store) =>
      requestResult(store.put(encodeRecord(record), RECORD_ID))
    );
    const readBack = await readRecord();
    if (!readBack.found || readBack.record === null) return await notVerified();
    // The device key is the one read back, so this also proves it survived being stored.
    const verifyWith =
      readBack.record.kind === "device" ? readBack.record.wrappingKey : aesKey;
    const decrypted = await decryptKey(verifyWith, readBack.record);
    if (decrypted === null || !sameKey(decrypted, key))
      return await notVerified();
    return { status: "saved" };
  } catch (error) {
    return { status: "failed", failure: toFailure(error, "writeFailed") };
  }
};

/** What this device holds: nothing, a key (device mode), a record waiting for its passphrase, or a failure. */
export const readRememberedKey = async (): Promise<ReadOutcome> => {
  const unavailable = vaultUnavailableReason();
  if (unavailable !== null) return { status: "failed", failure: unavailable };
  try {
    const read = await readRecord();
    if (!read.found) return { status: "empty" };
    if (read.record === null)
      return { status: "failed", failure: { kind: "notReadable" } };
    if (read.record.kind === "passphrase")
      return { status: "locked", record: read.record };
    const key = await decryptKey(read.record.wrappingKey, read.record);
    return key === null
      ? { status: "failed", failure: { kind: "notDecryptable" } }
      : { status: "remembered", key };
  } catch (error) {
    return { status: "failed", failure: toFailure(error, "storageRefused") };
  }
};

/**
 * Derives the key from the passphrase and decrypts. AES-GCM cannot tell a
 * wrong passphrase from a changed ciphertext; both read as a wrong passphrase.
 */
export const unlockRememberedKey = async (
  record: LockedRecord,
  passphrase: string
): Promise<UnlockOutcome> => {
  const unavailable = vaultUnavailableReason();
  if (unavailable !== null) return { status: "failed", failure: unavailable };
  try {
    const aesKey = await derivePassphraseKey(
      passphrase,
      record.salt,
      record.iterations
    );
    const key = await decryptKey(aesKey, record);
    return key === null
      ? { status: "wrongPassphrase" }
      : { status: "unlocked", key };
  } catch (error) {
    return { status: "failed", failure: toFailure(error, "storageRefused") };
  }
};

/** Deletes the record (ciphertext, iv, salt and the device key with it). Nothing stored is a success. */
export const forgetRememberedKey = async (): Promise<WipeOutcome> => {
  if (typeof indexedDB === "undefined" || indexedDB === null)
    return { status: "wiped" };
  try {
    await inStore("readwrite", (store) =>
      requestResult(store.delete(RECORD_ID))
    );
    return { status: "wiped" };
  } catch (error) {
    const failure = toFailure(error, "writeFailed");
    return {
      status: "failed",
      failure: {
        kind: "wipeFailed",
        detail: "detail" in failure ? failure.detail : failure.kind,
      },
    };
  }
};

/** The failure as the clause after "because" / ":" in a status sentence. */
export const describeVaultFailure = (failure: VaultFailure): string => {
  switch (failure.kind) {
    case "noIndexedDb":
      return "this browser gives the page no IndexedDB (a private window, or site data blocked for this site)";
    case "noWebCrypto":
      return "this browser gives the page no WebCrypto (it needs the page served over HTTPS)";
    case "storageRefused":
      return `the browser refused its storage for this site ("${failure.detail}")`;
    case "storageFull":
      return "the browser's storage for this site is full";
    case "writeFailed":
      return `the browser could not write it ("${failure.detail}")`;
    case "verifyFailed":
      return "reading it back after saving did not give the same key";
    case "notDecryptable":
      return "it could not be decrypted (this browser's site data was cleared or changed, so its device key is gone)";
    case "notReadable":
      return "what is stored is not a key this page wrote";
    case "wipeFailed":
      return `the browser could not delete it ("${failure.detail}")`;
  }
};
